import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Field, Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { IMedicalCardForm } from "./medicalCardForm.interface";
import { Modal } from "../../Modal";
import {
  IMedicalCardQualification,
  IDiagnosisICD10FormItem,
  RoutesEnum,
  userRoles,
  isRequired,
  MedicalCardTemplateTypesEnum,
  MedicalCardTemplateTypes,
} from "../../../shared";
import { useAppSelector } from "../../../hooks";
import styles from "./medicalCardForm.module.scss";
import { delete_icon, logo } from "@jat-med/assets";
import { Button } from "../../Buttons";

import { DatePickerField } from "../../FormFields";
import { MedicalCardTemplatesDialog } from "../../Dialogs";

export const MedicalCardForm: FC<
  IMedicalCardForm<IMedicalCardQualification>
> = ({ onSubmit, patient, initialValues }) => {
  const { t } = useTranslation();

  const currentUser = useAppSelector(
    ({ userReducer }) => userReducer.currentUser,
  );

  const [selectedFile, setSelectedFile] = useState<File>();
  const [preview, setPreview] = useState<string | File[]>();
  const [showModal, setShowModal] = useState(false);

  const [templateConfig, setTemplateConfig] = useState<{
    key: MedicalCardTemplateTypes;
    fieldName: string;
    currentValue:
      | string
      | string[]
      | { code: string; text: string }[]
      | IDiagnosisICD10FormItem[];
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDocumentOwner = currentUser?._id === initialValues?.doctor?._id;
  const isCreate = !initialValues;
  const canEdit =
    currentUser?.role === userRoles.doctor && (isCreate || isDocumentOwner);

  const onSelectFile = (e: any) => {
    if (!e || e.length === 0) {
      setSelectedFile(undefined);
      return;
    }
    setSelectedFile(e[0]);
  };

  const handleAddTemplate = ({
    key,
    fieldName,
    currentValue,
  }: {
    key: MedicalCardTemplateTypes;
    fieldName: string;
    currentValue:
      | string
      | string[]
      | { code: string; text: string }[]
      | IDiagnosisICD10FormItem[];
  }) => {
    setTemplateConfig({ key, fieldName, currentValue });
  };

  useEffect(() => {
    if (!selectedFile) {
      setPreview(initialValues?.imagingTestsImageLink);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [initialValues?.imagingTestsImageLink, selectedFile]);

  const formInitialValues = useMemo(
    () => ({
      visitDate: new Date().toISOString(),
      ...initialValues,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialValues],
  );

  return (
    <div className={styles.form_wrap}>
      <Form
        onSubmit={onSubmit}
        initialValues={formInitialValues}
        render={({ handleSubmit, form, submitting, pristine, values }) => (
          <form
            onSubmit={handleSubmit}
            className={`${!canEdit && styles.disabled_form}`}
          >
            <div className={`${styles.field_group_wrap} ${styles.form_header}`}>
              <div className={styles.place_and_date_wrap}>
                <Field name="city" validate={isRequired}>
                  {({ input, meta }) => (
                    <div className={`${styles.input_wrap}`}>
                      <label htmlFor="city">
                        {t("adminPanel.form.cityLabel")}
                      </label>
                      <input
                        {...input}
                        id="city"
                        required
                        type="text"
                        placeholder={t("adminPanel.form.cityLabel")}
                      />
                    </div>
                  )}
                </Field>
                {values.visitDate && (
                  <div className={styles.date_wrap}>
                    <label>{t("adminPanel.form.visitDateLabel")}</label>
                    <Field
                      name="visitDate"
                      component={DatePickerField}
                      showTimeSelect={false}
                      placeholderText={t("adminPanel.form.chooseDate")}
                      setChosenDate={(value: Date) => {
                        form.change("visitDate", value);
                      }}
                      minDate={new Date("2020-01-01")}
                      chosenDate={new Date(values.visitDate)}
                      disabled={!canEdit}
                    />
                  </div>
                )}
              </div>
              <div className={styles.consultation_type_wrap}>
                <img src={logo} alt="logo" />

                <Field name="typeOfConsultation" validate={isRequired}>
                  {({ input, meta }) => (
                    <div className={`${styles.input_wrap}`}>
                      <label htmlFor="typeOfConsultation">
                        {t(`medCard.consultationType`)}
                      </label>
                      <div className={styles.input_with_template}>
                        <input
                          {...input}
                          id="typeOfConsultation"
                          placeholder={`${t(`medCard.consultationType`)}...`}
                          required
                        />
                        <Button
                          disabled={!canEdit}
                          label={`+ ${t("medCard.template")}`}
                          className="round_dashed_button"
                          customStyle={styles.add_template_button}
                          onClick={() =>
                            handleAddTemplate({
                              key: MedicalCardTemplateTypesEnum.TYPE_OF_CONSULTATION,
                              fieldName: "typeOfConsultation",
                              currentValue: values?.typeOfConsultation || "",
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </Field>
              </div>
            </div>
            {initialValues?.doctor?.firstName && (
              <div className={`${styles.field_group_wrap} ${styles.user_data}`}>
                <p className={styles.field_group_title}>
                  {t("userRolesLocales.doctor")}
                </p>
                <div className={styles.data_item}>
                  <p>
                    {initialValues?.doctor?.firstName}{" "}
                    {initialValues?.doctor?.lastName}
                  </p>
                </div>
              </div>
            )}
            <div className={`${styles.field_group_wrap} ${styles.user_data}`}>
              <p className={styles.field_group_title}>
                {t("medCard.patientData")}
              </p>
              <div className={styles.data_item}>
                <p className={styles.label}>{t("adminPanel.fullName")}</p>
                <Link
                  to={`${RoutesEnum.admin}${RoutesEnum.patients}/${patient?._id}`}
                >{`${patient?.firstName} ${patient?.lastName}`}</Link>
              </div>
              <div className={styles.data_item}>
                <p className={styles.label}>{t("adminPanel.pesel")}</p>
                <p>{patient?.idCode}</p>
              </div>
              <div className={styles.data_item}>
                <p className={styles.label}>{t("adminPanel.phone")}</p>
                <p>{patient?.phoneNumber}</p>
              </div>
            </div>

            <Field name="medicalHistory">
              {({ input, meta }) => (
                <div
                  className={`${styles.field_group_wrap} ${styles.input_wrap}`}
                >
                  <div className={styles.field_group_title_wrap}>
                    <label
                      htmlFor="medicalHistory"
                      className={styles.field_group_title}
                    >
                      {t(`medCard.medicalHistory`)}
                    </label>
                    <Button
                      disabled={!canEdit}
                      label={`+ ${t("medCard.template")}`}
                      className="round_dashed_button"
                      customStyle={styles.add_template_button}
                      onClick={() =>
                        handleAddTemplate({
                          key: MedicalCardTemplateTypesEnum.MEDICAL_HISTORY,
                          fieldName: "medicalHistory",
                          currentValue: values?.medicalHistory || "",
                        })
                      }
                    />
                  </div>
                  <textarea
                    {...input}
                    id="medicalHistory"
                    placeholder={`${t(`medCard.medicalHistory`)}...`}
                  />
                </div>
              )}
            </Field>
            <Field name="Manifestations">
              {({ input, meta }) => (
                <div
                  className={`${styles.field_group_wrap} ${styles.input_wrap}`}
                >
                  <div className={styles.field_group_title_wrap}>
                    <label
                      htmlFor="manifestations"
                      className={styles.field_group_title}
                    >
                      {t(`medCard.manifestation`)}
                    </label>
                    <Button
                      disabled={!canEdit}
                      label={`+ ${t("medCard.template")}`}
                      className="round_dashed_button"
                      customStyle={styles.add_template_button}
                      onClick={() =>
                        handleAddTemplate({
                          key: MedicalCardTemplateTypesEnum.MANIFESTATION,
                          fieldName: "Manifestations",
                          currentValue: values?.Manifestations || "",
                        })
                      }
                    />
                  </div>
                  <textarea
                    {...input}
                    id="manifestations"
                    placeholder={`${t(`medCard.manifestation`)}...`}
                  />
                </div>
              )}
            </Field>
            <div className={`${styles.field_group_wrap} `}>
              <p className={styles.field_group_title}>
                {t(`medCard.neurologicalTests`)}
              </p>
              <div className={styles.field_wrap}>
                <p className={styles.field_item_title}>{t("Siła")}</p>
                <div className={styles.strength_items_wrap}>
                  <div className={styles.strength_items}>
                    <Field name="strength.pkg">
                      {({ input, meta }) => (
                        <div className={`${styles.input_wrap}`}>
                          <label htmlFor="strengthPkg">{t("pkg")}</label>
                          <input
                            {...input}
                            id="strengthPkg"
                            type="text"
                            placeholder={t("pkg")}
                          />
                        </div>
                      )}
                    </Field>
                    <Field name="strength.lkg">
                      {({ input, meta }) => (
                        <div className={`${styles.input_wrap}`}>
                          <label htmlFor="strengthLkg">{t("lkg")}</label>
                          <input
                            {...input}
                            id="strengthLkg"
                            type="text"
                            placeholder={t("lkg")}
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                  <div className={styles.strength_items}>
                    <Field name="strength.pkd">
                      {({ input, meta }) => (
                        <div className={`${styles.input_wrap}`}>
                          <label htmlFor="strengthPkd">{t("pkd")}</label>
                          <input
                            {...input}
                            id="strengthPkd"
                            type="text"
                            placeholder={t("pkd")}
                          />
                        </div>
                      )}
                    </Field>
                    <Field name="strength.lkd">
                      {({ input, meta }) => (
                        <div className={`${styles.input_wrap}`}>
                          <label htmlFor="strengthLkd">{t("lkd")}</label>
                          <input
                            {...input}
                            id="strengthLkd"
                            type="text"
                            placeholder={t("lkd")}
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                </div>

                <span>wg Lovetta</span>
              </div>
              <div className={styles.fields_grid_wrap}>
                <div className={styles.field_wrap}>
                  <label
                    htmlFor="objawOsiowy"
                    className={styles.field_item_title}
                  >
                    {t("Objaw osiowy")}
                  </label>
                  <Field name="objawOsiowy">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <input
                          {...input}
                          id="objawOsiowy"
                          type="text"
                          placeholder={t("+/-")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Mackiewicza")}
                  </p>
                  <Field name="symptomMackiewicza.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomMackiewiczaPkd">
                          {t("pkd")}
                        </label>
                        <input
                          {...input}
                          id="symptomMackiewiczaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomMackiewicza.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomMackiewiczaLkd">
                          {t("lkd")}
                        </label>
                        <input
                          {...input}
                          id="symptomMackiewiczaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <span>st.</span>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Spurlinga")}
                  </p>
                  <Field name="symptomSpurlinga.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomSpurlingaPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomSpurlingaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomSpurlinga.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomSpurlingaLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomSpurlingaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Lasègue’a")}
                  </p>
                  <Field name="symptomLaseguea.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomLasegueaPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomLasegueaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomLaseguea.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomLasegueaLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomLasegueaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <span>st.</span>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Bikelesa")}
                  </p>
                  <Field name="symptomBikelelsa.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomBikelesaPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomBikelesaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomBikelelsa.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomBikelesaLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomBikelesaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Krzem.-Fajersztajna")}
                  </p>
                  <Field name="SymptomKrzemienieckiFajersztajna.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomKrzemienieckiFajersztajnaPkd">
                          {t("pkd")}
                        </label>
                        <input
                          {...input}
                          id="symptomKrzemienieckiFajersztajnaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="SymptomKrzemienieckiFajersztajna.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomKrzemienieckiFajersztajnaLkd">
                          {t("lkd")}
                        </label>
                        <input
                          {...input}
                          id="symptomKrzemienieckiFajersztajnaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Hoffmana")}
                  </p>
                  <Field name="symptomHoffmana.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomHoffmanaPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomHoffmanaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomHoffmana.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomHoffmanaLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomHoffmanaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Patricka")}
                  </p>
                  <Field name="SymptomPatricka.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomPatrickaPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomPatrickaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="SymptomPatricka.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomPatrickaLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomPatrickaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <label
                    htmlFor="symptomLhermittea"
                    className={styles.field_item_title}
                  >
                    {t("Objaw Lhermitte'a")}
                  </label>
                  <Field name="symptomLhermittea">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <input
                          {...input}
                          id="symptomLhermittea"
                          type="text"
                          placeholder={t("+/-")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>{t("Objaw Fire")}</p>
                  <Field name="symptomFire.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomFirePkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomFirePkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomFire.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomFireLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomFireLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Trendelenburga")}
                  </p>
                  <Field name="symptomTrendelenburga.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomTrendelenburgaPkd">
                          {t("pkd")}
                        </label>
                        <input
                          {...input}
                          id="symptomTrendelenburgaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomTrendelenburga.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomTrendelenburgaLkd">
                          {t("lkd")}
                        </label>
                        <input
                          {...input}
                          id="symptomTrendelenburgaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Babińskiego")}
                  </p>
                  <Field name="symptomBabinskiego.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomBabinskiegoPkd">
                          {t("pkd")}
                        </label>
                        <input
                          {...input}
                          id="symptomBabinskiegoPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomBabinskiego.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomBabinskiegoLkd">
                          {t("lkd")}
                        </label>
                        <input
                          {...input}
                          id="symptomBabinskiegoLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Duchenne'a")}
                  </p>
                  <Field name="symptomDuchennea.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomDuchenneaPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomDuchenneaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomDuchennea.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomDuchenneaLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomDuchenneaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Oppenheima")}
                  </p>
                  <Field name="symptomOppenheima.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomOppenheimaPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomOppenheimaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomOppenheima.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomOppenheimaLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomOppenheimaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Wassermana")}
                  </p>
                  <Field name="symptomWassermana.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomWassermanaPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomWassermanaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomWassermana.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomWassermanaLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomWassermanaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Gordona")}
                  </p>
                  <Field name="symptomGordona.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomGordonaPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomGordonaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomGordona.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomGordonaLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomGordonaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div></div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Objaw Schäffera")}
                  </p>
                  <Field name="symptomSchaffera.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomSchafferaPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="symptomSchafferaPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="symptomSchaffera.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="symptomSchafferaLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="symptomSchafferaLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
              </div>
            </div>
            <div className={`${styles.field_group_wrap} `}>
              <p className={styles.field_group_title}>
                {t("medCard.reflexes")}
              </p>
              <div className={styles.fields_grid_wrap}>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Odruch biceps")}
                  </p>
                  <Field name="bicepsReflex.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="bicepsReflexPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="bicepsReflexPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="bicepsReflex.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="bicepsReflexLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="bicepsReflexLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Odruch Hamstrings")}
                  </p>
                  <Field name="hamstringsReflex.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="hamstringsReflexPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="hamstringsReflexPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="hamstringsReflex.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="hamstringsReflexLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="hamstringsReflexLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>

                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Odruch triceps")}
                  </p>
                  <Field name="tricepsReflex.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="tricepsReflexPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="tricepsReflexPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="tricepsReflex.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="tricepsReflexLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="tricepsReflexLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Odruch Achillesa")}
                  </p>
                  <Field name="achillesReflex.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="achillesReflexPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="achillesReflexPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="achillesReflex.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="achillesReflexLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="achillesReflexLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className={styles.field_wrap}>
                  <p className={styles.field_item_title}>
                    {t("Odruch kolanowy")}
                  </p>
                  <Field name="kneeReflex.pkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="kneeReflexPkd">{t("pkd")}</label>
                        <input
                          {...input}
                          id="kneeReflexPkd"
                          type="text"
                          placeholder={t("pkd")}
                        />
                      </div>
                    )}
                  </Field>
                  <Field name="kneeReflex.lkd">
                    {({ input, meta }) => (
                      <div className={`${styles.input_wrap}`}>
                        <label htmlFor="kneeReflexLkd">{t("lkd")}</label>
                        <input
                          {...input}
                          id="kneeReflexLkd"
                          type="text"
                          placeholder={t("lkd")}
                        />
                      </div>
                    )}
                  </Field>
                </div>
              </div>
            </div>
            <div className={styles.field_group_wrap}>
              <Field name="imagingTests">
                {({ input, meta }) => (
                  <div className={`${styles.input_wrap}`}>
                    <div className={styles.field_group_title_wrap}>
                      <label
                        htmlFor="imagingTests"
                        className={styles.field_group_title}
                      >
                        {t("medCard.imagingTests")}
                      </label>
                      <Button
                        disabled={!canEdit}
                        label={`+ ${t("medCard.template")}`}
                        className="round_dashed_button"
                        customStyle={styles.add_template_button}
                        onClick={() =>
                          handleAddTemplate({
                            key: MedicalCardTemplateTypesEnum.IMAGING_TEST,
                            fieldName: "imagingTests",
                            currentValue: values.imagingTests || "",
                          })
                        }
                      />
                    </div>
                    <textarea
                      {...input}
                      id="imagingTests"
                      placeholder={`${t("medCard.imagingTests")}...`}
                    />
                  </div>
                )}
              </Field>
              <Field type="file" name="imagingTestsImage">
                {({ input: { value, onChange, ...input }, meta }) => (
                  <div className={styles.input_preview_wrap}>
                    <div
                      className={`${styles.input_file_wrap} ${styles.input_wrap}`}
                    >
                      <label htmlFor="imagingTestsImage">
                        {t("adminPanel.form.photoLabel")}
                      </label>
                      <input
                        {...input}
                        ref={fileInputRef}
                        onChange={({ target }) => {
                          onSelectFile(target.files);
                          onChange(target.files);
                        }}
                        type="file"
                        id="imagingTestsImage"
                        name="imagingTestsImage"
                        accept="image/*"
                        className={styles.hidden_file_input}
                      />
                      <div className={styles.file_input_custom}>
                        <button
                          type="button"
                          className={styles.file_choose_button}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {t("adminPanel.form.chooseFile")}
                        </button>
                        <span className={styles.file_name_label}>
                          {selectedFile
                            ? selectedFile.name
                            : t("adminPanel.form.noFileSelected")}
                        </span>
                      </div>
                    </div>
                    {preview && (
                      <div
                        className={styles.preview}
                        onClick={() => setShowModal(true)}
                      >
                        <img src={preview as string} alt="downloaded_image" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreview("");
                            setSelectedFile(undefined);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                            // @ts-ignore
                            form.change("imagingTestsImage", undefined);
                            // @ts-ignore
                            form.change("imagingTestsImageLink", undefined);
                          }}
                          className={styles.delete_button}
                        >
                          <img src={delete_icon} alt="delete_btn" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </Field>
            </div>

            <div className={`${styles.field_group_wrap} ${styles.input_wrap}`}>
              <div className={styles.field_group_title_wrap}>
                <label
                  htmlFor="diagnosisICD10"
                  className={styles.field_group_title}
                >
                  {t("medCard.diagnosis")}
                </label>
                <div className={styles.badge}>ICD-10</div>
                <Button
                  onClick={() =>
                    handleAddTemplate({
                      key: MedicalCardTemplateTypesEnum.ICD_10,
                      fieldName: "diagnosisICD10s",
                      currentValue: (values.diagnosisICD10s || []) as any,
                    })
                  }
                  disabled={!canEdit}
                  label={`+`}
                  className="round_dashed_button"
                />
                {values.diagnosisICD10s &&
                  values.diagnosisICD10s?.length > 0 && (
                    <div className={styles.icd_badge_list_wrap}>
                      {(
                        values.diagnosisICD10s as IDiagnosisICD10FormItem[]
                      )?.map((item) => (
                        <button
                          key={item.code}
                          className={styles.icd_badge_item}
                          onClick={() => {
                            const updatedArray = (
                              values.diagnosisICD10s as IDiagnosisICD10FormItem[]
                            ).filter((i) => i._id !== item._id);
                            form.change("diagnosisICD10s", updatedArray);
                          }}
                        >
                          <p>{item.code} </p>
                          <span>&#10005;</span>
                        </button>
                      ))}
                    </div>
                  )}
              </div>
              {values.diagnosisICD10s && values.diagnosisICD10s?.length > 0 && (
                <div className={styles.icd_list_wrap}>
                  {(values.diagnosisICD10s as IDiagnosisICD10FormItem[])?.map(
                    (item) => (
                      <div key={item.code} className={styles.icd_item}>
                        <p className={styles.icd_item_header}>
                          <span>{item.code}</span>—<span>{item.title}</span>
                        </p>
                        <textarea
                          id={`diagnosisICD10-${item.code}`}
                          disabled={!canEdit}
                          value={item.text || ""}
                          placeholder={`${t("medCard.diagnosis")}...`}
                          onChange={(e) => {
                            const updated = (
                              values.diagnosisICD10s as IDiagnosisICD10FormItem[]
                            ).map((i) =>
                              i.code === item.code
                                ? { ...i, text: e.target.value }
                                : i,
                            );
                            form.change("diagnosisICD10s", updated as any);
                          }}
                        />
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
            <div className={`${styles.field_group_wrap} ${styles.input_wrap}`}>
              <div className={styles.field_group_title_wrap}>
                <p className={styles.field_group_title}>
                  {t("medCard.procedure")}
                </p>
                <div className={styles.badge}>ICD-9</div>
                <Button
                  onClick={() =>
                    handleAddTemplate({
                      key: MedicalCardTemplateTypesEnum.ICD_9,
                      fieldName: "procedureICD9",
                      currentValue: (values.procedureICD9 || []) as any,
                    })
                  }
                  disabled={!canEdit}
                  label={t("+")}
                  className="round_dashed_button"
                />
                {values.procedureICD9 && values.procedureICD9?.length > 0 && (
                  <div className={styles.icd_badge_list_wrap}>
                    {(values.procedureICD9 as any[])?.map((item) => (
                      <button
                        key={item._id || item.code}
                        className={styles.icd_badge_item}
                        onClick={() => {
                          const updatedArray = (
                            values.procedureICD9 as any[]
                          ).filter(
                            (i) =>
                              (i._id || i.code) !== (item._id || item.code),
                          );

                          form.change("procedureICD9", updatedArray as any);
                        }}
                      >
                        <p>{item.code} </p>
                        <span>&#10005;</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {values.procedureICD9 && values.procedureICD9?.length > 0 && (
                <div className={styles.icd_list_wrap}>
                  {(values.procedureICD9 as any[])?.map((item) => (
                    <p
                      key={item._id || item.code}
                      className={`${styles.icd_item} ${styles.icd_item_icd_9}`}
                    >
                      <span>{item.code}</span>—
                      <span>{item.title || item.text}</span>
                    </p>
                  ))}
                </div>
              )}
            </div>
            <Field name="qualifications">
              {({ input, meta }) => (
                <div
                  className={`${styles.field_group_wrap} ${styles.input_wrap}`}
                >
                  <div className={styles.field_group_title_wrap}>
                    <label
                      htmlFor="kwalifikacje"
                      className={styles.field_group_title}
                    >
                      {t("medCard.qualification")}
                    </label>
                    <Button
                      disabled={!canEdit}
                      label={`+ ${t("medCard.template")}`}
                      className="round_dashed_button"
                      customStyle={styles.add_template_button}
                      onClick={() =>
                        handleAddTemplate({
                          key: MedicalCardTemplateTypesEnum.QUALIFICATION,
                          fieldName: "qualifications",
                          currentValue: values.qualifications || "",
                        })
                      }
                    />
                  </div>
                  <textarea
                    {...input}
                    id="kwalifikacje"
                    placeholder={`${t("medCard.qualification")}...`}
                  />
                </div>
              )}
            </Field>
            <Field name="recommended">
              {({ input, meta }) => (
                <div
                  className={`${styles.field_group_wrap} ${styles.input_wrap}`}
                >
                  <div className={styles.field_group_title_wrap}>
                    <label
                      htmlFor="zalecono"
                      className={styles.field_group_title}
                    >
                      {t("medCard.recommended")}
                    </label>
                    <Button
                      disabled={!canEdit}
                      label={`+ ${t("medCard.template")}`}
                      className="round_dashed_button"
                      customStyle={styles.add_template_button}
                      onClick={() =>
                        handleAddTemplate({
                          key: MedicalCardTemplateTypesEnum.RECOMMENDED,
                          fieldName: "recommended",
                          currentValue: values.recommended || "",
                        })
                      }
                    />
                  </div>
                  <textarea
                    {...input}
                    id="zalecono"
                    placeholder={`${t("medCard.recommended")}...`}
                  />
                </div>
              )}
            </Field>

            <div className={styles.buttons_wrap}>
              {currentUser?.role === userRoles.doctor && (
                <Button
                  type="submit"
                  disabled={submitting || !canEdit}
                  label={t("buttons.save")}
                  className="orange"
                />
              )}
            </div>
            <MedicalCardTemplatesDialog
              templateKey={templateConfig?.key || null}
              onClose={() => setTemplateConfig(null)}
              onSelect={(tpl) => {
                if (templateConfig) {
                  const isIcdField =
                    templateConfig.key ===
                      MedicalCardTemplateTypesEnum.ICD_10 ||
                    templateConfig.key === MedicalCardTemplateTypesEnum.ICD_9;
                  const isTypeOfConsultationField =
                    templateConfig.key ===
                    MedicalCardTemplateTypesEnum.TYPE_OF_CONSULTATION;

                  if (isTypeOfConsultationField) {
                    form.change(
                      templateConfig.fieldName as keyof IMedicalCardQualification,
                      tpl.title,
                    );
                  } else if (isIcdField) {
                    const currentArray = Array.isArray(
                      templateConfig.currentValue,
                    )
                      ? templateConfig.currentValue
                      : [];

                    const alreadyExists = currentArray.some(
                      (item: any) => item._id === tpl.id,
                    );

                    if (tpl.id && !alreadyExists) {
                      const updatedArray: IDiagnosisICD10FormItem[] = [
                        ...(currentArray as IDiagnosisICD10FormItem[]),
                        {
                          _id: tpl.id,
                          code: tpl.code,
                          title: tpl.title,
                          text: tpl.text,
                        },
                      ];
                      form.change(
                        templateConfig.fieldName as keyof IMedicalCardQualification,
                        updatedArray as any,
                      );
                    }
                  } else {
                    form.change(
                      templateConfig.fieldName as keyof IMedicalCardQualification,
                      tpl.text,
                    );
                  }

                  setTemplateConfig(null);
                }
              }}
            />
          </form>
        )}
      />

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
        {preview && (
          <div
            className={styles.modal_preview}
            onClick={() => setShowModal(true)}
          >
            <img src={preview as string} alt="downloaded_image" />
          </div>
        )}
      </Modal>
    </div>
  );
};
